/* The basemap's colour, derived rather than hand-picked.
 *
 * `01` and `14` section 9.3 both say the same sentence: "Basemap desaturated to
 * Slate/Steel; only data is saturated." That is more specific than "make it
 * grey", and the difference is the whole reason this file exists. Slate
 * (#2a3b48) and Steel (#5a6b78) are blue-greys - both carry a hue spread of 30
 * - while Protomaps' `grayscale` theme is literally neutral: #a3a3a3, #cccccc,
 * #c2c2c2, zero spread on every value. Neutral satisfies "desaturated" and
 * misses "to Slate/Steel".
 *
 * So every colour keeps its LIGHTNESS and takes Steel's HUE. Mixing toward
 * Steel was the obvious approach and is wrong: Steel is darker than most of the
 * grayscale ramp, so a mix drags the whole basemap down - #cccccc lands at
 * #b3b7ba - and Protomaps separates features BY lightness. Earth is lighter
 * than background, buildings lighter than earth. Darkening them all
 * indiscriminately compresses the separation the theme is built on.
 *
 * Steel rather than Slate as the hue source because they are the same hue
 * family and Steel is the lighter of the two. Only the hue is taken, so the
 * choice matters less than it looks; Slate at #2a3b48 is an ink, and nothing
 * here adopts its lightness.
 *
 * EVERY HEX IN THE THEME IS CONVERTED, not a hand-maintained list of keys. A
 * list would be wrong the first time the package adds a colour - silently, as
 * one neutral patch among tinted ones - and there are 74 keys to get wrong.
 * Walking the object cannot go stale.
 *
 * Why derive at all, rather than write the tinted values down: two copies of a
 * colour diverge the day after they are made, and the source of truth for Steel
 * is tokens.css, read at call time via readMapColors() because MapLibre paint
 * properties are canvas config and cannot read a CSS custom property.
 */
import { namedTheme } from "protomaps-themes-base";
import type { Theme } from "protomaps-themes-base";
import { readMapColors } from "./mapColors";

/* How much of Steel's saturation to apply. Steel is only 14% saturated to begin
 * with, so this lands the basemap around 8% - a hue that is legible against a
 * neutral grey side by side, and still leaves the risk ramp as the only
 * saturated colour on the screen. */
const SATURATION = 0.6;

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

function toRgb(hex: string): [number, number, number] | null {
  if (!HEX.test(hex)) return null;
  let h = hex.slice(1);
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
  const n = parseInt(h, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

function rgbToHsl([r, g, b]: [number, number, number]): [number, number, number] {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h, s, l];
}

function hueToRgb(p: number, q: number, t: number): number {
  let x = t;
  if (x < 0) x += 1;
  if (x > 1) x -= 1;
  if (x < 1 / 6) return p + (q - p) * 6 * x;
  if (x < 1 / 2) return q;
  if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
  return p;
}

function hslToHex(h: number, s: number, l: number): string {
  let r: number, g: number, b: number;
  if (s === 0) {
    r = g = b = l;
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hueToRgb(p, q, h + 1 / 3);
    g = hueToRgb(p, q, h);
    b = hueToRgb(p, q, h - 1 / 3);
  }
  const hex = (v: number) =>
    Math.max(0, Math.min(255, Math.round(v * 255)))
      .toString(16)
      .padStart(2, "0");
  return `#${hex(r)}${hex(g)}${hex(b)}`;
}

/* Not every member of Theme is a colour: 77 are strings, and `pois` and
 * `landcover` are objects. This is the subset whose values are strings, so the
 * assignment below is checked rather than cast past - the two object members
 * come through the spread untouched, which is what should happen to them. */
type ColourKey = { [K in keyof Theme]-?: Theme[K] extends string ? K : never }[keyof Theme];

/* Only the NEUTRALS are tinted. A theme value that already carries a hue was
 * chosen deliberately and keeps its own; overriding those would be recolouring
 * the theme rather than tinting it. In grayscale that is every colour, but the
 * rule is what makes this safe against a future release that adds one. */
export function slateSteelTheme(): Theme {
  const steel = toRgb(readMapColors().steel);
  const [steelHue, steelSat] = steel ? rgbToHsl(steel) : [0, 0];

  const base = namedTheme("grayscale");
  const out: Theme = { ...base };

  for (const key of Object.keys(base) as (keyof Theme)[]) {
    const value = base[key];
    if (typeof value !== "string") continue;
    const rgb = toRgb(value);
    if (!rgb) continue;
    const [, sat, light] = rgbToHsl(rgb);
    if (sat !== 0) continue;
    out[key as ColourKey] = hslToHex(steelHue, steelSat * SATURATION, light);
  }

  return out;
}
